import { useMemo } from 'react';

import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import { FilterType } from '../../../filters/types/filters';
import { getNumericIdFromNestedId } from '../../../filters/utils/get-numeric-id-from-nested-id';
import { usePageConfig } from '../../PageConfigProvider/PageConfigProvider';
import type { PositionedItem } from '../../PageConfigProvider/types/item';
import { usePageState } from '../../PageState/PageStateProvider';

const COMPANY_LEVEL_ID = 'company';

export type PrimaryCompany = { rcid: number; name: string };

/** Every company chip driving the page, in chip order. */
export const usePrimaryCompanies = (): PrimaryCompany[] => {
  const effectiveFilters = useEffectiveFilters();
  const primaryFilter = effectiveFilters.find((filter) => filter.isPrimary);
  return useMemo(() => {
    if (primaryFilter?.type !== FilterType.TREE) return [];
    const chips = primaryFilter.primarySelections?.length
      ? primaryFilter.primarySelections
      : primaryFilter.selections;
    return chips.flatMap((chip) => {
      if (chip.levelId !== COMPANY_LEVEL_ID) return [];
      const rcid = getNumericIdFromNestedId(chip.id);
      return rcid === null ? [] : [{ rcid, name: chip.name }];
    });
  }, [primaryFilter]);
};

const isSubsidiaryChart = (candidate: PositionedItem) =>
  (candidate.plot?.chartType === 'icicle' ||
    candidate.plot?.chartType === 'timeline') &&
  candidate.plot.secondaryDimension === 'subsidiaries';

const useSubsidiaryChartId = (): string | undefined => {
  const { config } = usePageConfig();
  return useMemo(
    () =>
      config.tabs
        .flatMap((tab) => tab.tabContent.items ?? [])
        .find(isSubsidiaryChart)?.id,
    [config.tabs]
  );
};

export const usePrimaryEntityKey = (
  item: PositionedItem | undefined
): string | undefined => {
  const subsidiaryChartId = useSubsidiaryChartId();
  return item !== undefined && isSubsidiaryChart(item)
    ? (subsidiaryChartId ?? item.id)
    : item?.id;
};

/**
 * The one company a single-entity card shows: the one picked in the page's
 * subsidiaries charts, which share one switcher selection. Else the first
 * chip; null when no company is selected.
 */
export const usePrimaryCompany = (): PrimaryCompany | null => {
  const companies = usePrimaryCompanies();
  const { pageState } = usePageState();
  const subsidiaryChartId = useSubsidiaryChartId();
  const switched =
    subsidiaryChartId === undefined
      ? undefined
      : pageState.primaryEntities[subsidiaryChartId];
  const picked =
    switched?.levelId === COMPANY_LEVEL_ID
      ? companies.find((company) => String(company.rcid) === switched.id)
      : undefined;
  return picked ?? companies[0] ?? null;
};
